/**
 * Sdílené kousky modulu Správa (revize Nastavení, 2026-10-12).
 *
 * Barva je barva sekce Řízení — jen na text, linku, pruh a rámeček, nikdy jako
 * podklad plochy (brandbook studené modré zakazuje, viz `uiTokens.tableSurfaces`).
 * Vybraný řádek proto nese neutrální plochu + akcentní pruh vlevo.
 */
import { SKLO_SVETLE } from '../../lib/uiTokens';

export const SECTION_COLOR = '#3B82F6';
export const CERVENA = '#EF4444';
export const ZELENA = '#059669';

export const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';
export const hlavicky = () => ({ 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() });

/** GET → JSON; chyba = odmítnutý slib s textem pro člověka. */
export async function nacti(url) {
    const r = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!r.ok) throw new Error(await chybaZOdpovedi(r));
    return r.json();
}

/** PUT/POST/DELETE s JSON tělem. */
export async function posli(url, metoda, data) {
    const r = await fetch(url, { method: metoda, headers: hlavicky(), credentials: 'same-origin', body: data === undefined ? undefined : JSON.stringify(data) });
    if (!r.ok) throw new Error(await chybaZOdpovedi(r));
    return r.status === 204 ? null : r.json().catch(() => null);
}

async function chybaZOdpovedi(r) {
    try {
        const d = await r.json();
        if (d?.errors) return Object.values(d.errors).flat()[0];
        return d?.error || d?.message || `Chyba ${r.status}`;
    } catch {
        return r.status === 403 ? 'Na tohle nemáte oprávnění.' : `Chyba ${r.status}`;
    }
}

export function stylKarty(isDark) {
    return isDark ? {
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
}

export function stylPole(isDark) {
    return {
        width: '100%', height: 38, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13.5,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
}

export const tlumene = (isDark) => (isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)');
export const cara = (isDark) => (isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)');

/** Nadpis sekce v kartě — kapitálky nese jen nadpis, popisky ne (norma §1c). */
export function Nadpis({ children, isDark, mt = 0, vpravo = null }) {
    return (
        <div className="flex items-center" style={{ margin: `${mt}px 0 8px`, gap: 8 }}>
            <p style={{ flex: 1, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tlumene(isDark), margin: 0 }}>{children}</p>
            {vpravo}
        </div>
    );
}

/** Popisek nad hodnotou (norma §1c, pilot Lidé). */
export function Pole({ popisek, children, isDark }) {
    return (
        <div style={{ padding: '8px 0' }}>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 3px' }}>{popisek}</p>
            <div style={{ fontSize: 13.5, color: 'var(--text-primary)', minHeight: 20 }}>{children ?? <span style={{ color: tlumene(isDark) }}>—</span>}</div>
        </div>
    );
}

/** Řádek „popis · hodnota" pro třetí sloupec (norma §1f). */
export function RadekPanelu({ popis, hodnota, isDark, barva = null }) {
    return (
        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara(isDark)}` }}>
            <span style={{ color: tlumene(isDark) }}>{popis}</span>
            <b style={{ color: barva || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{hodnota}</b>
        </div>
    );
}

/** Sekundární tlačítko (obrys). */
export function Tlacitko({ children, onClick, isDark, Icon = null, disabled = false, plna = false, title }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} title={title} className="inline-flex items-center justify-center gap-1.5 rounded-lg"
            style={{
                height: 36, padding: '0 14px', fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
                background: plna ? SECTION_COLOR : 'transparent', color: plna ? '#fff' : 'var(--text-primary)',
                border: plna ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}`,
                width: plna === 'cela' ? '100%' : undefined,
            }}>
            {Icon && <Icon size={14} />} {children}
        </button>
    );
}

/** Patička modalu: Zrušit · hlavní akce (na mobilu přes celou šířku, norma §1c). */
export function PatickaModalu({ onZrusit, onPotvrdit, potvrdit = 'Uložit', ukladam = false, isDark, isMobile }) {
    const spolecne = { height: isMobile ? 44 : 40, padding: '0 16px', fontSize: 13.5, borderRadius: 8, cursor: 'pointer', flex: isMobile ? 1 : undefined };
    return (
        <div className="flex gap-2 justify-end">
            <button type="button" onClick={onZrusit} style={{ ...spolecne, background: 'transparent', color: 'var(--text-secondary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                Zrušit
            </button>
            <button type="button" onClick={onPotvrdit} disabled={ukladam} style={{ ...spolecne, fontWeight: 600, background: SECTION_COLOR, color: '#fff', border: 'none', opacity: ukladam ? 0.6 : 1 }}>
                {ukladam ? 'Ukládám…' : potvrdit}
            </button>
        </div>
    );
}
